import { sub } from "date-fns";
import { groupBy } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  AuditLogFilterType,
  AuditLogForwardPaginationQueryVariables,
  ComparisonOperator,
  useGetAuditLogFilterSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "../Filter/FilterMenu";
import { useSnackbar } from "notistack";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "../Filter/QuickFilterMenu/QuickFilterMenu";

export type AuditlogFilterVariables = Pick<
  AuditLogForwardPaginationQueryVariables,
  "actionFilter" | "identityFilter" | "timestampFilter"
>;
export type UseAuditLogFilterContextParams = {
  scopeMrn: string;
};

export type UseAuditLogFilterContextOutput = {
  filterVariables: AuditlogFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
};

export function useAuditLogFilterContext({
  scopeMrn,
}: UseAuditLogFilterContextParams): UseAuditLogFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const actionSuggestions = useAuditlogActionSuggestions(scopeMrn);
  const subjectSuggestions = useAuditlogSubjectSuggestions(scopeMrn);
  const timeSuggestions = useAuditlogTimeSuggestions("");

  const actionFilterParam = searchParams.get("action");
  const subjectFilterParam = searchParams.get("subject");
  const timeFilterParam = searchParams.get("time");
  const searchFilterParam = searchParams.get("queryTerm");

  const actionFilter = actionFilterParam ? actionFilterParam : undefined;

  const identityFilter: AuditLogForwardPaginationQueryVariables["identityFilter"] =
    subjectFilterParam
      ? {
          name: subjectFilterParam,
        }
      : undefined;

  const availableTimeFilters = getAvailableTimeFilters();

  const isValidTimeFilterParam = (
    param: any,
  ): param is keyof typeof availableTimeFilters => {
    return Object.keys(availableTimeFilters).includes(param);
  };

  const timeFilter = isValidTimeFilterParam(timeFilterParam)
    ? availableTimeFilters[timeFilterParam]
    : undefined;
  const timestampFilter: AuditLogForwardPaginationQueryVariables["timestampFilter"] =
    timeFilter ? timeFilter.filterValue : undefined;

  const searchFilter = searchFilterParam ? searchFilterParam : undefined;

  const addFilter = (param: string, value: string) => {
    const url = new URL(location.href);
    url.searchParams.set(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string) => {
    const url = new URL(location.href);
    url.searchParams.delete(param);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = new URL(location.href);
    url.searchParams.delete("action");
    url.searchParams.delete("subject");
    url.searchParams.delete("time");
    url.searchParams.delete("queryTerm");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    actionFilter
      ? {
          label: "Action",
          value: actionFilter,
          onClick: () => removeFilter("action"),
        }
      : [],
    identityFilter?.name
      ? {
          label: "Subject",
          value: identityFilter.name,
          onClick: () => removeFilter("subject"),
        }
      : [],
    timeFilter
      ? {
          label: "Time",
          value: timeFilter.label,
          onClick: () => removeFilter("time"),
        }
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useAuditlogSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      enqueueSnackbar(
        "Freeform search text is not supported at this time. Please choose a provided value.",
        { variant: "error" },
      );
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Action",
      activeCount: actionFilter ? 1 : 0,
      menuOptions: quickMenuOptions("action", actionSuggestions.suggestions),
      onMenuOpen: actionSuggestions.fetchSuggestions,
    },
    {
      label: "Subject",
      activeCount: identityFilter ? 1 : 0,
      menuOptions: quickMenuOptions("subject", subjectSuggestions.suggestions),
      onMenuOpen: subjectSuggestions.fetchSuggestions,
    },
    {
      label: "Time",
      activeCount: timeFilter ? 1 : 0,
      menuOptions: quickMenuOptions("time", timeSuggestions.suggestions),
    },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  const filterVariables = useMemo(() => {
    return {
      ...(actionFilter ? { actionFilter } : {}),
      ...(identityFilter ? { identityFilter } : {}),
      ...(timestampFilter ? { timestampFilter } : {}),
    };
  }, [actionFilterParam, subjectFilterParam, timeFilterParam]);

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useAuditlogSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
) {
  const categorySuggestions = useAuditlogCategorySuggestions(searchInput);
  const actionSuggestions = useAuditlogActionSuggestions(scopeMrn);
  const subjectSuggestions = useAuditlogSubjectSuggestions(scopeMrn);
  const timeSuggestions = useAuditlogTimeSuggestions(searchInput);
  switch (field) {
    case "action":
      return actionSuggestions;
    case "subject":
      return subjectSuggestions;
    case "time":
      return timeSuggestions;
    default:
      return categorySuggestions;
  }
}

function useAuditlogCategorySuggestions(searchInput: string) {
  const actionFilterGroup: FilterMenuOption = {
    field: "action",
    label: "Action",
    value: "action",
  };

  const subjectFilterGroup: FilterMenuOption = {
    field: "subject",
    label: "Subject",
    value: "subject",
  };

  const timeFilterGroup: FilterMenuOption = {
    field: "time",
    label: "Time",
    value: "time",
  };

  const suggestions = [
    actionFilterGroup,
    subjectFilterGroup,
    timeFilterGroup,
  ].filter((s) =>
    s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
  );
  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useAuditlogActionSuggestions(scopeMrn: string) {
  const actionGroupBy = (action: string) => {
    const parts = action.split(".");
    const shortName = parts.pop() || "";
    const groupName = parts.join(".");
    return [groupName, shortName] as const;
  };

  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAuditLogFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        resourceMrn: scopeMrn,
        filterType: AuditLogFilterType.Action,
        searchText,
      },
    });
  };

  const param = "action";

  const suggestionsData =
    (data ?? previousData)?.auditLogSuggestions.flatMap((suggestion, index) => {
      if (suggestion.value.__typename === "StringValue") {
        const [groupName] = actionGroupBy(suggestion.value.stringValue);
        return {
          param,
          field: `action.${index}`,
          label: suggestion.label,
          value: suggestion.value.stringValue,
          group: groupName,
        };
      }
      return [];
    }) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function getAvailableTimeFilters() {
  const now = new Date();
  return {
    BEFORE_NOW: {
      label: "Before now",
      value: "BEFORE_NOW",
      filterValue: {
        operator: ComparisonOperator.Lt,
        timestamp: now.toISOString(),
      },
    },
    PAST_24_HOURS: {
      label: "Past 24 hours",
      value: "PAST_24_HOURS",
      filterValue: {
        operator: ComparisonOperator.Gt,
        timestamp: sub(now, { hours: 24 }).toISOString(),
      },
    },
    PAST_WEEK: {
      label: "Past week",
      value: "PAST_WEEK",
      filterValue: {
        operator: ComparisonOperator.Gt,
        timestamp: sub(now, { weeks: 1 }).toISOString(),
      },
    },
    PAST_MONTH: {
      label: "Past month",
      value: "PAST_MONTH",
      filterValue: {
        operator: ComparisonOperator.Gt,
        timestamp: sub(now, { months: 1 }).toISOString(),
      },
    },
    PAST_YEAR: {
      label: "Past year",
      value: "PAST_YEAR",
      filterValue: {
        operator: ComparisonOperator.Gt,
        timestamp: sub(now, { years: 1 }).toISOString(),
      },
    },
  };
}

function useAuditlogSubjectSuggestions(scopeMrn: string) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAuditLogFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        resourceMrn: scopeMrn,
        filterType: AuditLogFilterType.Identity,
        searchText,
      },
    });
  };

  const param = "subject";

  const suggestionsData =
    (data ?? previousData)?.auditLogSuggestions.flatMap((suggestion, index) => {
      if (suggestion.value.__typename === "StringValue") {
        const field = `subject.${index}`;
        return {
          param,
          field,
          label: suggestion.label,
          value: suggestion.label,
          group: suggestion.label[0]?.toLocaleUpperCase(),
        };
      }
      return [];
    }) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAuditlogTimeSuggestions(searchInput: string) {
  const availableTimeFilters = getAvailableTimeFilters();
  const suggestions = Object.entries(availableTimeFilters)
    .map(([value, { label }]) => {
      return { label, value };
    })
    .map((s, index) => ({ ...s, field: `time.${index}`, param: "time" }))
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );

  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}
